import { type ComponentChildren, createContext } from "preact";
import { useContext } from "preact/hooks";
import type { Roll } from "../../dice/pool";

// What a chip rolls: the notation and, as the dice bar's params do, the range that makes it a success (a roll-under
// test), whether it is an attack (the adjustment adds to it, and a natural 20 or 1 is critical) and the description
// the feed shows, and the most the dice bar's bonus may move it.
export type RollSpec = { instruction: string; success?: string; attack?: boolean; description: string; limit?: number };

// What a view rolls with, and the chip's own wording ("Rolar ..."). A chip outside one is only its number.
export type Roller = { roll(spec: RollSpec): Promise<Roll | null>; title(spec: RollSpec): string };

export const RollContext = createContext<Roller | null>(null);

const SIZES = {
	sm: "min-h-[30px] min-w-[44px] px-[9px] text-[15px]",
	md: "min-h-9 min-w-[44px] px-3 text-lg",
	lg: "min-h-[46px] min-w-16 px-3 text-3xl leading-tight",
	xl: "min-h-14 min-w-20 px-2.5 text-[58px] leading-[54px]",
};

const FRAME = "gap-1 rounded-lg border border-line-strong bg-surface font-bold text-ink";
// What character_sheet.css's .cs-tap and .cs-num give the chip, spelled out for the example, which Mapas shows without that sheet.
const FRAME_BASE = "inline-flex items-center justify-center font-serif tabular-nums";

// The chip's frame, shared with the chances written inside a rule's prose, which carry no size of their own.
export const CHIP_CLASS = `cs-tap cs-num ${FRAME} hover:border-carmesim-600 hover:bg-surface-sunken active:translate-y-px`;

// A rollable number: a framed figure. Red dotted text is for glossary terms. `framed` keeps the frame where nothing
// rolls, for an example of a chip.
export function RollChip({
	spec,
	size = "md",
	class: className = "",
	framed = false,
	children,
}: {
	spec: RollSpec | null;
	size?: keyof typeof SIZES;
	class?: string;
	framed?: boolean;
	children: ComponentChildren;
}) {
	const roller = useContext(RollContext);
	if (!spec || !roller) return <span class={framed ? `${FRAME_BASE} ${FRAME} ${SIZES[size]} ${className}` : className}>{children}</span>;
	const title = roller.title(spec);
	return (
		<button type="button" title={title} class={`${CHIP_CLASS} ${SIZES[size]} ${className}`} onClick={() => void roller.roll(spec)}>
			<span class="sr-only">{title}: </span>
			{children}
		</button>
	);
}
